import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { LoginPanel } from './LoginPanel';

interface LoginDialogProps {
  open: boolean;
  /** Where a login that leaves the site (Espotz Google redirect) should come back to. */
  returnTo?: string | null;
  /** Closed without logging in. */
  onDismiss: () => void;
  /** Logged in (by any route, incl. the dev password modal); the caller resumes. */
  onLoggedIn: () => void;
}

/**
 * The login panel in a dialog, for actions on a page (register, react, enrol): the visitor logs
 * in and stays where they were (Espotz-docs#2038, S3). A user whose profile isn't complete is sent
 * to the wizard first, as the full /login page does.
 */
export function LoginDialog({
  open,
  returnTo,
  onDismiss,
  onLoggedIn,
}: LoginDialogProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (!open || !isAuthenticated || !user) return;
    onLoggedIn();
    if (user.isProfileComplete === false)
      navigate('/complete-profile', { replace: true });
  }, [isAuthenticated, navigate, onLoggedIn, open, user]);

  return (
    <Dialog open={open} onOpenChange={next => !next && onDismiss()}>
      <DialogContent className='max-w-sm' data-testid='login-dialog'>
        <DialogTitle className='sr-only'>{t('login.a11y.panel')}</DialogTitle>
        <DialogDescription className='sr-only'>
          {t('login.subtitle')}
        </DialogDescription>
        <LoginPanel variant='dialog' returnTo={returnTo} />
      </DialogContent>
    </Dialog>
  );
}
